import type { PropsWithChildren } from 'react';
import React from 'react';

import { trackError } from '../utils/trackError';
import LeasesHeaderNav from './LeasesHeaderNav';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { getErrorPageAction } from '../utils/getErrorPageTextProps';
import { ErrorPage } from '@zg-rentals/error-page-components';
import type { QueryError } from '../utils/queries';

type State = {
  appIsBroken: boolean;
  error: QueryError | null;
};

/**
 * Component that renders an error if the app is an error state. Else, it renders its children
 */
export class ErrorCatcher extends React.Component<PropsWithChildren<{}>, State> {
  state: State = {
    appIsBroken: false,
    error: null,
  };

  componentDidCatch(error, info) {
    RpErrorBoundary.logError(error, info);
    trackError(error, info);

    this.setState({
      appIsBroken: true,
      error: error,
    });
  }

  getErrorAction() {
    const { error } = this.state;

    if (error?.actionText && error?.actionUrl) {
      return <a href={error.actionUrl}>{error.actionText}</a>;
    }

    return getErrorPageAction();
  }

  getErrorDetails() {
    const { error } = this.state;

    const errorMessage =
      error?.userMessage ||
      'You may not have access to this lease or the link has expired. Check the link and try again later.';

    return errorMessage;
  }

  render() {
    const { children } = this.props;
    const { appIsBroken, error } = this.state;

    const errorHeader = error?.message || 'Could not load your lease';

    return appIsBroken ? (
      <LeasesHeaderNav>
        <ErrorPage errorMessage={errorHeader} errorDetails={this.getErrorDetails()} action={this.getErrorAction()} />
      </LeasesHeaderNav>
    ) : (
      children
    );
  }
}

export default ErrorCatcher;
